import React, { useEffect, useRef, useState } from 'react';
import { useNavigation, useRoute } from '@react-navigation/native';

import Loader from '~/components/core/Loader';
import ScreenWrapper from '../features/Theme/ScreenWrapper';
import CreateStrategyWizard, { CreateStrategyWizardHandle } from '../features/AIStrategies/CreateStrategyWizard';
import { cloneSourceFromStrategy } from '../features/AIStrategies/utils/seeding';
import AIStrategyBuilderService from '~/api-services/AIStrategyBuilderService';
import { StrategyInputNode, StrategyTemplate } from '~/api-services/AIStrategyBuilderService/types';

export default function CreateAIStrategyScreen() {
    const navigation = useNavigation<any>();
    const wizardRef = useRef<CreateStrategyWizardHandle>(null);
    // Set once the wizard has agreed to the leave, so re-dispatching it is not intercepted again.
    const leavingRef = useRef(false);
    const route = useRoute<any>();
    const templateUuid: string | undefined = route.params?.templateUuid;
    // Clone flow: only the source strategy's id travels through navigation params; the strategy
    // is re-fetched here and its template + input nodes derived (cloneSourceFromStrategy).
    const cloneStrategyId: string | undefined = route.params?.cloneStrategyId;

    const [template, setTemplate] = useState<StrategyTemplate | undefined>(undefined);
    const [cloneNodes, setCloneNodes] = useState<StrategyInputNode[] | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(!!templateUuid || !!cloneStrategyId);

    useEffect(() => {
        if (!templateUuid) {
            return;
        }
        AIStrategyBuilderService.getStrategyTemplate(templateUuid)
            .then(({ data }) => {
                if (data?.status && data.returnData) {
                    setTemplate(data.returnData);
                }
            })
            .catch(() => {
                // ignore — form will render in custom mode
            })
            .finally(() => setLoading(false));
    }, [templateUuid]);

    // Every way out of this screen — header back, hardware back, swipe, a tab press — funnels
    // through beforeRemove, so the wizard is asked once, here, rather than per control.
    useEffect(
        () =>
            navigation.addListener('beforeRemove', (e: any) => {
                if (leavingRef.current) {
                    return;
                }
                e.preventDefault();
                wizardRef.current?.requestExit(() => {
                    leavingRef.current = true;
                    navigation.dispatch(e.data.action);
                });
            }),
        [navigation]
    );

    useEffect(() => {
        if (!cloneStrategyId) {
            return;
        }
        // Cloning rebuilds the wizard from the submitted node list, so `input` must be asked for.
        AIStrategyBuilderService.getStrategy(cloneStrategyId, { returnInputJSON: true })
            .then(({ data }) => {
                if (data?.status && data.returnData) {
                    const source = cloneSourceFromStrategy(data.returnData);
                    setTemplate(source.template);
                    setCloneNodes(source.cloneNodes);
                }
            })
            .catch(() => {
                // ignore — form will render in custom mode
            })
            .finally(() => setLoading(false));
    }, [cloneStrategyId]);

    if (loading) {
        return (
            <ScreenWrapper>
                <Loader loading />
            </ScreenWrapper>
        );
    }

    return (
        <ScreenWrapper>
            <CreateStrategyWizard
                ref={wizardRef}
                onCreated={(strategyId?: string) =>
                    // Replace (not push): back from the in-progress detail returns to the list,
                    // not the spent wizard.
                    strategyId ? navigation.replace('AIStrategyDetails', { strategyId }) : navigation.goBack()
                }
                onClose={() => navigation.goBack()}
                template={template}
                cloneNodes={cloneNodes}
            />
        </ScreenWrapper>
    );
}
